// Consent state for Google Consent Mode v2.
//
// The German privacy policy (/text_blocks.collection/privacy_policy.de.md) is the binding
// description of this behaviour: analytics and marketing tags must stay inert
// until the visitor opts in, the choice must be revocable at any time, and
// Google's tags must be told about it through Consent Mode v2.
//
// Nothing from Google is fetched before a grant. Denied-by-default Consent Mode
// would already stop the cookies, but "erst nach Ihrer Einwilligung aktiviert"
// in the policy promises more than that, so the script tags are injected on
// grant rather than loaded inert.

// Only `analytics` gates a script load, because the GTM container is the single
// tag on the site. `marketing` still has to be recorded separately: it is what
// the Consent Mode update turns into ad_storage, which decides whether the Ads
// conversion inside that container may store anything.
export type ConsentState = {
  analytics: boolean;
  marketing: boolean;
};

export const NO_CONSENT: ConsentState = { analytics: false, marketing: false };
export const FULL_CONSENT: ConsentState = { analytics: true, marketing: true };

// Bumping this re-asks everyone. Required when the purposes change, because a
// consent given for the old set is not consent for the new one.
const STORAGE_KEY = "fr_consent_v1";

type StoredConsent = ConsentState & { decidedAt: string };

// Anything that is not exactly two booleans is not a record of a decision.
// Coercing instead would read the string "false" as a grant and load the tags,
// and would read a malformed object as a rejection and stop asking.
const isStoredConsent = (value: unknown): value is StoredConsent =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as StoredConsent).analytics === "boolean" &&
  typeof (value as StoredConsent).marketing === "boolean";

export const readConsent = (): ConsentState | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isStoredConsent(parsed)) return null;
    return { analytics: parsed.analytics, marketing: parsed.marketing };
  } catch {
    // Private-mode localStorage throws on access. Treat it as "not decided":
    // the banner shows, tags stay off, and nothing is tracked.
    return null;
  }
};

export const writeConsent = (state: ConsentState): void => {
  try {
    const stored: StoredConsent = { ...state, decidedAt: new Date().toISOString() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Choice cannot be persisted, but must still apply for this page view.
  }
  applyConsent(state);
};

// localStorage is shared across tabs but Google's consent state lives in each
// tab's dataLayer, so a withdrawal in one tab would leave the others granted
// until they happen to reload. A null key is a localStorage.clear().
export const watchConsentChanges = (
  onChange: (state: ConsentState | null) => void,
): (() => void) => {
  const handler = (event: StorageEvent) => {
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    const state = readConsent();
    applyConsent(state ?? NO_CONSENT);
    onChange(state);
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
};

// Withdrawal has to actually remove what was set. A Consent Mode update alone
// stops Google reading the cookies but leaves them in the browser for up to two
// years, which reads as ignored consent to anyone inspecting the site.
const GOOGLE_COOKIE_PREFIXES = ["_ga", "_gid", "_gcl", "_gac"];

const clearGoogleCookies = (): void => {
  const domain = window.location.hostname;
  // The tags set cookies on the registrable domain, so an expiry on the exact
  // hostname alone silently misses them.
  const domains = [domain, `.${domain}`, `.${domain.split(".").slice(-2).join(".")}`];
  document.cookie.split(";").forEach((entry) => {
    const name = entry.split("=")[0]?.trim();
    if (!name || !GOOGLE_COOKIE_PREFIXES.some((prefix) => name.startsWith(prefix))) return;
    domains.forEach((scope) => {
      document.cookie = `${name}=; path=/; domain=${scope}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    });
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  });
};

function pushToDataLayer(entry: unknown): void {
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push(entry);
}

// Google recognises a gtag command only when the pushed entry is an `arguments`
// object: the container tests `[object Arguments]`, and routes a plain array to
// an unrelated legacy path that calls `window[name]` and swallows the resulting
// error. A command pushed as an array is therefore ignored in silence, so it has
// to go through this shim - the same one as Google's published snippet. Events
// are different: those are plain objects, which is their documented form.
function gtag(..._args: unknown[]): void {
  // Reading `arguments` is the point rather than a shortcut, so the rest
  // parameter cannot be forwarded in its place: it is an array, and an array is
  // what Google ignores.
  // eslint-disable-next-line prefer-rest-params
  pushToDataLayer(arguments);
}

// The consent currently in force, kept in memory rather than re-read from
// storage: a grant still governs this page view when localStorage is
// unavailable, and consulting storage would discard it.
let currentConsent: ConsentState | null = null;

// Events are announced on the dataLayer and mapped to tags inside the GTM
// container, so a conversion label is never baked into a build.
//
// Dropped unless consent is in force, which the absence of a container does not
// achieve on its own: the container replays the entire dataLayer when it loads,
// so an event pushed before a decision would be processed retroactively by a
// later accept, and after a withdrawal the container stays in the page and goes
// on answering its triggers. Both purposes are required because the container
// serves analytics and a conversion both; today the banner is all-or-nothing, so
// they are never unequal.
//
// Every tag in the container can read these parameters, so pass only values that
// come from the site's own content - never anything a visitor typed.
export const trackEvent = (event: string, params?: Record<string, string>): void => {
  if (!currentConsent?.analytics || !currentConsent.marketing) return;
  pushToDataLayer({ event, ...params });
};

const granted = (value: boolean) => (value ? "granted" : "denied");

// Google's tags read consent off the dataLayer, so the update has to be pushed
// whether or not any tag has loaded yet - a tag injected later replays it.
export const applyConsent = (state: ConsentState): void => {
  currentConsent = state;
  gtag("consent", "update", {
    ad_storage: granted(state.marketing),
    ad_user_data: granted(state.marketing),
    ad_personalization: granted(state.marketing),
    analytics_storage: granted(state.analytics),
  });
  if (!state.analytics || !state.marketing) clearGoogleCookies();
  loadTags(state);
};

const GTM_ID = process.env.GATSBY_GTM_ID;

const injected = new Set<string>();

const isInjected = (key: string): boolean =>
  injected.has(key) || !!document.querySelector(`script[data-consent-tag="${key}"]`);

const injectScript = (key: string, src: string): void => {
  if (isInjected(key)) return;
  injected.add(key);
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  script.setAttribute("data-consent-tag", key);
  document.head.appendChild(script);
};

// The container hosts GA4 and the Google Ads conversion, so analytics consent
// loads it and the Consent Mode update above decides what it may store.
//
// applyConsent runs again whenever the banner is reopened and re-accepted. The
// bootstrap event must not be re-pushed into an already-loaded container: it
// retriggers every Page View tag, double-counting pageviews and conversions.
const loadTags = (state: ConsentState): void => {
  if (!state.analytics || !GTM_ID || isInjected("gtm")) return;
  pushToDataLayer({ "gtm.start": Date.now(), event: "gtm.js" });
  injectScript("gtm", `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_ID)}`);
};

// With no container configured the banner would ask for permission to do
// nothing, which is its own dark pattern.
export const hasConfiguredTags = (): boolean => !!GTM_ID;

// The footer control and the banner sit in unrelated component trees - the
// banner is mounted from the Gatsby wrappers, the footer is a slice - so they are
// connected here rather than through props. The trigger is passed along so focus
// can be returned to it once a decision closes the banner.
let openSettings: ((trigger: HTMLElement) => void) | null = null;

export const onOpenConsentSettings = (handler: (trigger: HTMLElement) => void): (() => void) => {
  openSettings = handler;
  return () => {
    if (openSettings === handler) openSettings = null;
  };
};

export const openConsentSettings = (trigger: HTMLElement): void => {
  openSettings?.(trigger);
};
